import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu"; // Import Radix DropdownMenu
import CompanyIcon from "../../icons/CompanyIcon";
import HrIcon from "../../icons/HrIcon";
import AnalyticsIcon from "../../icons/AnalyticsIcon";
import { ChevronDownIcon as LucideChevronDownIcon } from "lucide-react"; // Renamed to avoid conflict if you create your own
import "../../css/Fonts.css";
import TickIcon from "../../icons/TickIcon";

const NavigationDropdown = () => {
  // Radix DropdownMenu can manage its own open state
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      title: "Company",
      subtitle: "Data analysis",
      icon: CompanyIcon,
      path: "/summary",
    },
    {
      title: "Hr",
      subtitle: "Creating and editing candidates",
      icon: HrIcon,
      path: "/hr",
    },
    {
      title: "Analytics",
      subtitle: "Entering data for candidates",
      icon: AnalyticsIcon,
      path: "/analytics",
    },
  ];

  const defaultIconComponent = CompanyIcon;

  const getCurrentMenuItem = () => {
    if (location.pathname === "/") {
      return {
        title: "Organization",
        icon: defaultIconComponent,
      };
    }
    return (
      menuItems.find((item) => item.path === location.pathname) || {
        title: "Organization",
        icon: defaultIconComponent,
      }
    );
  };

  const currentMenuItem = getCurrentMenuItem();
  const CurrentIcon = currentMenuItem.icon;

  const handleMenuClick = (path) => {
    navigate(path);
    // Radix will close the menu on item click by default
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          // No onClick needed here to toggle, Radix handles it
          className="flex items-center bg-[#4318FF] text-white w-[150px] h-[40px] rounded-r-[50px] outline-none"
          aria-label="Navigation menu"
        >
          <div className="flex items-center justify-between w-full px-2.5">
            <div className="flex items-center gap-1">
              <CurrentIcon strokeColor="#FFFFFF" iconSize={16} />
              <span className="p-0 dropdown-button">
                {currentMenuItem.title}
              </span>
            </div>
            <LucideChevronDownIcon
              className="w-4 h-4 transition-transform duration-200 group-data-[state=open]:rotate-180" // Radix uses data attributes for state
            />
          </div>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="mt-1 w-[250px] bg-white rounded-lg shadow-lg overflow-hidden z-50"
          sideOffset={5} // Distance from the trigger
          align="left" // Aligns the content with the trigger
        >
          {menuItems.map((item) => {
            const ItemIcon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <DropdownMenu.Item
                key={item.title}
                onSelect={() => handleMenuClick(item.path)} // onSelect is the Radix way to handle item clicks
                className={`flex p-1 w-full items-center transition-colors text-left 
                           focus:outline-none
                           cursor-pointer select-none`}
                style={{ outline: "none" }}
              >
                <div
                  className={`flex p-2.5 w-full rounded-[4px] gap-1 items-center ${
                    isActive ? "bg-[#E2E7F3]" : "hover:bg-gray-200" // Active background
                    // If not active, data-[highlighted]:bg-gray-100 on parent Item will provide hover/focus style
                  }`}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="items-center flex gap-1">
                      <div className="items-center">
                        <ItemIcon strokeColor="#2B3674" iconSize={16} />
                      </div>
                      <div>
                        <div className="dropdown-item-title">{item.title}</div>
                        <div className="dropdown-item-subtitle">
                          {item.subtitle}
                        </div>
                      </div>
                    </div>
                    {isActive && <TickIcon />}
                  </div>
                </div>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default NavigationDropdown;
